/**
 * The viewer's own device identity — Ferruccio's gate on PR #83, B2:
 * "the viewer never enrolls, so every first arrival 401s." The merged
 * `workers/src/routes.ts` declares `POST /venns/:vennId/resolve`
 * `capability: 'device-auth'` (see `workers/src/router.ts`'s dispatcher:
 * a 'device-auth' route requires `Authorization: Bearer <device
 * secret>` BEFORE the handler ever runs, or the router itself denies
 * with `genericDenial()`) — resolve.ts's own module comment states the
 * sequencing plainly: "a visitor who does not yet hold a device secret
 * enrolls via `POST /devices` ... FIRST, exactly once." This viewer had
 * no enrollment step at all; every first-ever arrival's resolve call
 * carried no `Authorization` header and was denied by the router before
 * `resolve.ts`'s own code ever ran.
 *
 * WHERE THE SECRET LIVES, stated plainly rather than left implicit:
 * `enroll.ts`'s own comment names the two real answers — "Keychain/
 * Keystore (native) or `localStorage` (the web demo)" — and this IS the
 * web demo, so `localStorage` is the plan's own named choice here, not
 * an improvisation. What that means, honestly: `localStorage` is
 * per-BROWSER-PROFILE, not per-PERSON. On a shared machine (a library
 * computer, a family iPad's browser used by more than one person, a
 * kiosk), the device identity this module mints and stores is shared by
 * whoever next opens this site in that same browser profile — the same
 * tradeoff every browser-local credential carries, and one the web
 * platform gives no better answer to (there is no OS keychain a web
 * page can reach). This is a low-stakes identity in the design's own
 * terms — it authenticates "a device that once resolved a link," not a
 * person, and Mathew's own ruling on link security ("this is not a
 * particularly secure system... it's a toy") already accepts a
 * materially larger blast radius than a bank; a shared machine
 * inheriting one visitor's device identity for future venns it resolves
 * is a real, named consequence of that same choice, not a new one this
 * file introduces.
 */

export interface DeviceCredential {
  principal: string;
  secret: string;
}

const STORAGE_KEY = 'zov.device.v1';

/** The one `localStorage`-shaped operation this module needs, kept as
 *  an interface so device.ts stays testable without a real `window` —
 *  same discipline as hashSecret.ts's `FragmentSource`. */
export interface DeviceStorage {
  read(): string | null;
  write(value: string): void;
}

function isDeviceCredential(value: unknown): value is DeviceCredential {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as Record<string, unknown>).principal === 'string' &&
    typeof (value as Record<string, unknown>).secret === 'string'
  );
}

/** `null` on anything short of a well-formed stored credential —
 *  missing, corrupted, or written by some future/past incompatible
 *  shape. A caller that gets `null` back enrolls fresh; there is no
 *  partial-credential state this module tries to repair. */
export function loadStoredDevice(
  storage: DeviceStorage,
): DeviceCredential | null {
  const raw = storage.read();
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    return isDeviceCredential(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** Persists a freshly-enrolled (or freshly re-enrolled) credential.
 *  Best-effort: a write that fails (private browsing mode, storage
 *  quota, storage disabled entirely) is not fatal — it just means this
 *  tab enrolls again on its next visit, exactly like a first-ever visit
 *  today. */
export function storeDevice(
  storage: DeviceStorage,
  credential: DeviceCredential,
): void {
  storage.write(JSON.stringify(credential));
}

export function windowDeviceStorage(win: Window): DeviceStorage {
  return {
    read(): string | null {
      try {
        return win.localStorage.getItem(STORAGE_KEY);
      } catch {
        return null;
      }
    },
    write(value: string): void {
      try {
        win.localStorage.setItem(STORAGE_KEY, value);
      } catch {
        // Best-effort, per this module's own comment above.
      }
    },
  };
}
